// @ts-nocheck
/**
 * Edit Break — redesigned to obd3-gui pattern (ScrollPage + PageHeader +
 * Card + PageLoader). Duration validation (integer 1-1440) preserved.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Coffee, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { PageLoader } from '@/components/feedback/PageLoader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_BREAK_UPDATE, API_BREAK_GET_ONE } from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';

const EditBreak = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [breakData, setBreakData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const url = API_BREAK_GET_ONE.replace(':id', id);
    getApiCall(url, {})
      .then((resp: any) => setBreakData(resp || {}))
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          console.log(err);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'duration') {
      // Digits only (keyboard/paste already block -, +, ., e, but IME/mobile
      // keyboards can slip past those guards), capped at 4 chars and clamped
      // to the 1-1440 range so an out-of-range value can't even be typed.
      let sanitized = value.replace(/[^0-9]/g, '').slice(0, 4);
      if (sanitized !== '') {
        const num = Number(sanitized);
        if (num > 1440) sanitized = '1440';
        else if (num === 0) sanitized = '1';
      }
      setBreakData((prev: any) => ({ ...prev, duration: sanitized }));
      return;
    }

    setBreakData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!breakData.title?.trim()) {
      toast.error('Break Title is required');
      return;
    }
    if (!breakData.duration || breakData.duration.toString().trim() === '') {
      toast.error('Break Duration is required');
      return;
    }

    const durationNum = Number(breakData.duration);
    if (isNaN(durationNum)) {
      toast.error('Break Duration must be a valid number');
      return;
    }
    if (durationNum <= 0) {
      toast.error('Break Duration must be greater than 0');
      return;
    }
    if (durationNum > 1440) {
      toast.error('Break Duration must not exceed 1440 minutes (24 hours)');
      return;
    }
    if (!Number.isInteger(durationNum)) {
      toast.error('Break Duration must be a whole number (no decimals)');
      return;
    }

    setSaving(true);
    const url = API_BREAK_UPDATE.replace(':id', id);
    postApiCall(url, {
      title: breakData.title,
      duration: durationNum.toString(),
      description: breakData.description,
      type: breakData.type || 'Scheduled',
    })
      .then(() => {
        toast.success('Break updated successfully');
        setTimeout(() => navigate('/breaks'), 1000);
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to update break');
        }
        setSaving(false);
      });
  };

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Break"
        description="Update the name, duration, or description shown to agents."
        actions={
          <Button asChild variant="outline">
            <Link to="/breaks">
              <ArrowLeft className="h-4 w-4" />
              Back to breaks
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coffee className="h-4 w-4 text-primary" />
              Break details
            </CardTitle>
            <CardDescription>
              Whole minutes between 1 and 1440. Agents pick this break by title.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="title">
                Break title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                type="text"
                name="title"
                value={breakData.title || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">
                Duration (minutes) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="duration"
                type="number"
                name="duration"
                value={breakData.duration || ''}
                onChange={handleChange}
                min="1"
                max="1440"
                step="1"
                onKeyDown={(e: any) => {
                  if (
                    e.key === '-' ||
                    e.key === 'e' ||
                    e.key === '+' ||
                    e.key === '.'
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e: any) => {
                  const pastedValue = e.clipboardData.getData('text');
                  const numValue = Number(pastedValue);
                  if (
                    isNaN(numValue) ||
                    numValue <= 0 ||
                    numValue > 1440 ||
                    !Number.isInteger(numValue)
                  ) {
                    e.preventDefault();
                  }
                }}
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={3}
                name="description"
                value={breakData.description || ''}
                onChange={handleChange}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to="/breaks">Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default EditBreak;
